import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { User, Car, CreditCard, Check, Upload, ShieldCheck, Lock } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { PickupSelect } from "@/components/PickupSelect";

export default function Checkout() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [pickupFee, setPickupFee] = useState(0);
  const [pickupCustom, setPickupCustom] = useState(false);
  const start = sp.get("start"), end = sp.get("end");
  const [form, setForm] = useState({
    driver_name: "", driver_email: "", driver_phone: "", driver_license: "",
    license_photo: "", pickup_location: "", dropoff_location: "", consent_rgpd: false, promo_code: "",
  });

  useEffect(() => { api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(() => nav("/search")); }, [id, nav]);
  useEffect(() => {
    if (user) setForm((f) => ({ ...f, driver_name: f.driver_name || user.name, driver_email: f.driver_email || user.email }));
  }, [user]);

  if (!v) return <div className="min-h-[60vh] flex items-center justify-center text-canard font-body">{t("common.loading")}</div>;

  const days = Math.max(1, Math.round((new Date(end) - new Date(start)) / 86400000));
  const total = days * v.price_per_day;

  const uploadLicense = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!user) { toast.error("Connectez-vous pour téléverser le permis, ou renseignez le numéro."); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, license_photo: data.url });
      toast.success("Permis téléversé");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const submit = async () => {
    if (!form.consent_rgpd) { toast.error("Consentement RGPD requis"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        vehicle_id: v.id, start_date: start, end_date: end,
        pickup_location: form.pickup_location || v.city, dropoff_location: form.dropoff_location || v.city,
        pickup_is_custom: pickupCustom,
        driver_name: form.driver_name, driver_email: form.driver_email, driver_phone: form.driver_phone,
        driver_license: form.driver_license, license_photo: form.license_photo,
        consent_rgpd: form.consent_rgpd, origin_url: window.location.origin,
        promo_code: form.promo_code || null,
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); setSubmitting(false); }
  };

  const steps = [{ n: 1, icon: User, label: t("checkout.step1") }, { n: 2, icon: Car, label: t("checkout.step2") }, { n: 3, icon: CreditCard, label: t("checkout.step3") }];
  const canNext1 = form.driver_name && form.driver_email && form.driver_phone;

  return (
    <div className="section-sand min-h-screen py-10">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <h1 className="font-heading font-extrabold text-3xl text-ink mb-8 text-center">{t("checkout.title")}</h1>

        <div className="flex items-center justify-center gap-2 sm:gap-4 mb-10">
          {steps.map((s, i) => (
            <div key={s.n} className="flex items-center">
              <div className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full font-bold text-sm transition-all ${step >= s.n ? "bg-canard text-white" : "bg-white text-muted2 border border-border"}`}>
                {step > s.n ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
              {i < 2 && <div className={`w-6 sm:w-12 h-0.5 ${step > s.n ? "bg-canard" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 sm:p-8 shadow-card border border-border">
            {step === 1 && (
              <div className="space-y-4 animate-fade-up">
                {!user && <div className="bg-pastel/40 rounded-xl p-3 text-sm text-canard-dark font-semibold">🌴 {t("checkout.guest")} — aucun compte requis.</div>}
                <Field label={t("checkout.driverName")}><Input data-testid="checkout-name" value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} className="h-11 rounded-xl bg-sand" /></Field>
                <Field label={t("checkout.email")}><Input data-testid="checkout-email" type="email" value={form.driver_email} onChange={(e) => setForm({ ...form, driver_email: e.target.value })} className="h-11 rounded-xl bg-sand" /></Field>
                <Field label={t("checkout.phone")}><Input data-testid="checkout-phone" value={form.driver_phone} onChange={(e) => setForm({ ...form, driver_phone: e.target.value })} className="h-11 rounded-xl bg-sand" /></Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label={t("search.pickup")} testid="checkout-pickup"><PickupSelect island={v.country} value={form.pickup_location} onChange={(val) => setForm({ ...form, pickup_location: val })} testid="checkout-pickup-input" placeholder={v.city} onCustom={(isC, fee) => { setPickupCustom(isC); setPickupFee(isC ? fee : 0); }} /></Field>
                  <Field label={t("search.dropoff")} testid="checkout-dropoff"><PickupSelect island={v.country} value={form.dropoff_location} onChange={(val) => setForm({ ...form, dropoff_location: val })} testid="checkout-dropoff-input" placeholder={v.city} /></Field>
                </div>
                {form.pickup_location && (
                  <a data-testid="checkout-pickup-map" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${form.pickup_location} ${v.city || ""}`)}`}
                    target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden border border-border relative group">
                    <iframe title="pickup-map" className="w-full h-40 pointer-events-none" loading="lazy"
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(`${form.pickup_location} ${v.city || ""}`)}&output=embed`} />
                    <span className="absolute inset-x-0 bottom-0 bg-canard/90 text-white text-xs font-bold py-1.5 text-center group-hover:bg-canard-dark transition-colors">{t("pk.openMap")} ↗</span>
                  </a>
                )}
                <Button data-testid="checkout-next-1" disabled={!canNext1} onClick={() => setStep(2)} className="w-full h-11 rounded-full bg-canard hover:bg-canard-dark disabled:opacity-50">{t("checkout.next")}</Button>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-4 animate-fade-up">
                <Field label={t("checkout.license")} testid="checkout-license"><Input value={form.driver_license} onChange={(e) => setForm({ ...form, driver_license: e.target.value })} className="h-11 rounded-xl bg-sand" /></Field>
                <div>
                  <label className="text-sm font-bold text-ink">{t("checkout.licensePhoto")}</label>
                  <label className="mt-2 flex items-center gap-3 border-2 border-dashed border-border rounded-xl p-4 cursor-pointer hover:border-canard transition-colors" data-testid="checkout-license-upload-label">
                    <Upload className="w-5 h-5 text-canard" />
                    <span className="text-sm text-muted2 font-semibold">{form.license_photo ? "✓ Permis téléversé" : "Cliquez pour téléverser"}</span>
                    <input type="file" accept="image/*" onChange={uploadLicense} className="hidden" data-testid="checkout-license-input" />
                  </label>
                  <p className="text-xs text-muted2 mt-1">Le permis sera vérifié par le loueur au retrait du véhicule.</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="rounded-full flex-1">{t("checkout.back")}</Button>
                  <Button data-testid="checkout-next-2" onClick={() => setStep(3)} className="flex-1 rounded-full bg-canard hover:bg-canard-dark">{t("checkout.next")}</Button>
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-5 animate-fade-up">
                <div className="bg-pastel/30 rounded-xl p-4 flex gap-3">
                  <ShieldCheck className="w-6 h-6 text-emeraude shrink-0" />
                  <div className="text-sm text-canard-dark font-body">
                    <p className="font-bold">{t("vehicle.deposit")}: {v.deposit} €</p>
                    <p className="text-xs mt-1">{t("checkout.depositNote")}</p>
                  </div>
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={form.consent_rgpd} onCheckedChange={(c) => setForm({ ...form, consent_rgpd: c })} data-testid="checkout-rgpd-checkbox" className="mt-0.5" />
                  <span className="text-sm text-muted2 font-body">{t("checkout.rgpd")}</span>
                </label>
                <div>
                  <label className="text-sm font-bold text-ink">{t("promo.have")}</label>
                  <Input data-testid="checkout-promo-input" value={form.promo_code} onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })} placeholder={t("promo.code")} className="h-11 rounded-xl bg-sand mt-1 max-w-xs" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(2)} className="rounded-full flex-1">{t("checkout.back")}</Button>
                  <Button data-testid="checkout-pay-button" disabled={!form.consent_rgpd || submitting} onClick={submit}
                    className="flex-1 rounded-full bg-emeraude hover:bg-emeraude-dark shadow-cta disabled:opacity-50">
                    <Lock className="w-4 h-4 mr-1" /> {submitting ? "..." : `${t("checkout.pay")}`}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-card border border-border lg:sticky lg:top-24">
              <h3 className="font-heading font-bold text-lg text-ink mb-4">{t("checkout.summary")}</h3>
              <img src={v.photos?.[0]} alt="" className="w-full h-32 object-cover rounded-xl mb-3" />
              <p className="font-heading font-bold text-ink">{v.brand} {v.model}</p>
              <p className="text-xs text-muted2 mb-4">{start} → {end}</p>
              <div className="space-y-2 text-sm font-body border-t border-border pt-4">
                <div className="flex justify-between"><span className="text-muted2">{t("checkout.rental")} ({days} {t("checkout.days")})</span><span className="font-bold text-ink">{total.toFixed(2)} €</span></div>
                {pickupFee > 0 && <div className="flex justify-between" data-testid="checkout-pickup-fee"><span className="text-muted2">{t("pk.pickupFee")}</span><span className="font-bold text-ink">{pickupFee.toFixed(2)} €</span></div>}
                <div className="flex justify-between text-xs text-muted2"><span>{t("vehicle.deposit")}</span><span>{v.deposit} €</span></div>
                <div className="flex justify-between pt-2 border-t border-border"><span className="font-heading font-bold text-ink">{t("checkout.total")}</span><span className="font-heading font-extrabold text-canard-dark text-xl">{(total + pickupFee).toFixed(2)} €</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return <div data-testid={testid}><label className="text-sm font-bold text-ink">{label}</label><div className="mt-1">{children}</div></div>;
}
